import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, ArrowRight } from 'lucide-react'
import talkidaLogo from '../../assets/icons/logo.png'

const LandingHeader = ({ onGetStarted }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navItems = [
    { label: 'Features', href: '#features' },
    { label: 'Products', href: '/products' },
    { label: 'Our Clients', href: '#clients' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'About', href: '/about' },
    { label: 'Careers', href: '/careers' }
  ]

  const scrollToSection = (href) => {
    // Check if it's a route (starts with /)
    if (href.startsWith('/')) {
      navigate(href)
      setMobileMenuOpen(false)
      return
    }

    // If it's an anchor link (starts with #)
    if (href.startsWith('#')) {
      // If we're not on the landing page, navigate to landing page first
      if (location.pathname !== '/') {
        navigate(`/${href}`)
        // Wait for navigation and DOM update, then scroll
        setTimeout(() => {
          const element = document.querySelector(href)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      } else {
        // We're already on landing page, just scroll
        const element = document.querySelector(href)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }
      setMobileMenuOpen(false)
      return
    }

    setMobileMenuOpen(false)
  }

  // Handle scroll after navigation (when coming from another page)
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 300)
    }
  }, [location])

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            className="flex items-center gap-3 cursor-pointer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => navigate('/')}
          >
            <img src={talkidaLogo} alt="Talkida" className="h-10 w-auto" />
            <span className="text-2xl font-bold text-white">Talkida Technologies</span>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item, index) => (
              <motion.a
                key={item.label}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                onClick={(e) => {
                  if (!item.external) {
                    e.preventDefault()
                    scrollToSection(item.href)
                  }
                }}
                className="text-white/70 hover:text-white transition-colors duration-200 font-medium"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.label}
              </motion.a>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <motion.button
              onClick={onGetStarted}
              className="text-white/70 hover:text-white transition-colors duration-200 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Sign In
            </motion.button>

            <motion.button
              onClick={onGetStarted}
              className="group flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-150"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900/95 backdrop-blur-xl border-t border-white/10">
            <nav className="px-4 py-6 space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  onClick={(e) => {
                    if (!item.external) {
                      e.preventDefault()
                      scrollToSection(item.href)
                    } else {
                      setMobileMenuOpen(false)
                    }
                  }}
                  className="block text-white/70 hover:text-white transition-colors duration-200 font-medium py-2"
                >
                  {item.label}
                </a>
              ))}

              <div className="pt-4 space-y-3">
                <button
                  onClick={() => {
                    onGetStarted()
                    setMobileMenuOpen(false)
                  }}
                  className="block w-full text-center text-white/70 hover:text-white transition-colors duration-200 font-medium py-2"
                >
                  Sign In
                </button>

                <button
                  onClick={() => {
                    onGetStarted()
                    setMobileMenuOpen(false)
                  }}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full"
                >
                  Get Started Free
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </nav>
        </div>
      )}
    </header>
  )
}

export default LandingHeader

